"use client";

import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";

import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import useGetServiceInterfaceSettings from "@/hooks/requests/service-interface/use-get-service-interface-settings";
import { ServiceInterfaceMessages } from "@/messages/service-interface/service-interface.messages";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  serviceInterfaceId: number | null;
  messages: ServiceInterfaceMessages;
};

export function SettingsModal({ isOpen, onClose, serviceInterfaceId, messages }: Props) {
  const { data, isLoading, error } = useGetServiceInterfaceSettings({
    serviceInterfaceId,
    enabled: isOpen && serviceInterfaceId != null,
  });

  const settings = data?.status === "ok" ? data.items : [];
  const isEmpty = data?.status === "empty";

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{messages.settingsModalTitle}</DialogTitle>
      <DialogContent>
        {isLoading && <CircularProgress size={40} />}
        {!isLoading && error && (
          <MDTypography variant="body2" color="error">
            {error.message}
          </MDTypography>
        )}
        {!isLoading && !error && isEmpty && (
          <MDTypography variant="body2">{messages.settingsEmptyMessage}</MDTypography>
        )}
        {!isLoading && !error && !isEmpty && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{messages.settingsKeyColumn}</TableCell>
                <TableCell>{messages.settingsValueColumn}</TableCell>
                <TableCell>{messages.settingsDescriptionColumn}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {settings.map((setting) => (
                <TableRow key={setting.key}>
                  <TableCell>{setting.key}</TableCell>
                  <TableCell sx={{ wordBreak: "break-word" }}>{setting.value}</TableCell>
                  <TableCell sx={{ wordBreak: "break-word" }}>{setting.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant="gradient" color="dark" onClick={onClose}>
          {messages.settingsCloseButton}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
